/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ActionButton, Checkbox, ExternalLink, RadioButton, RadioButtonGroup, RadioGroupNew, RadioButtonNew, MarkdownEditor } from "@octopusdeploy/design-system-components";
import { NameSummaryWithSlug, SlugEditor, DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import type { ChannelResource, ChannelVersionRuleResource, DeploymentActionPackageResource, DeploymentActionResource, GitRefResource, ProjectResource, ValidateGitRefV2Response, ParentEnvironmentV2Resource, LifecycleResource, } from "@octopusdeploy/octopus-server-client";
import { deploymentActionPackages, displayName, isGitCommit, isGitTag, OctopusError, TenantedDeploymentMode, PersistenceSettingsType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { SetStateAction } from "react";
import * as React from "react";
import { useCallback } from "react";
import DesignRule from "~/areas/projects/components/Channels/DesignRule";
import { AdvancedTenantTagsSelector } from "~/components/AdvancedTenantSelector/AdvancedTenantSelector";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import { Feature, FeatureToggle } from "~/components/FeatureToggle";
import Markdown from "~/components/Markdown";
import MoreInfo from "~/components/MoreInfo/MoreInfo";
import RemovableExpandersList from "~/components/RemovableExpandersList";
import TagsList from "~/components/TagsList/TagsList";
import WarningPanel from "~/components/WarningPanel/WarningPanel";
import { ExpandableFormSection, FormSectionHeading, Note, Summary, Text } from "~/components/form";
import { CardFill } from "~/components/form/Sections/ExpandableFormSection";
import { required } from "~/components/form/Validators";
import DeploymentActionPackageMultiSelect, { DeploymentActionPackageReferenceDataItem } from "../../../../components/MultiSelect/DeploymentActionPackageMultiSelect";
import InternalLink from "../../../../components/Navigation/InternalLink/InternalLink";
import { ParentEnvironmentInfoPopover } from "../EphemeralEnvironments/EphemeralEnvironmentsInfoPopovers";
import { useEphemeralEnvironmentsEnabled } from "../EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { ChannelCustomFieldDefinitionsEdit } from "./ChannelCustomFieldDefinitionsEdit";
import { ChannelGitProtectionRulesEdit } from "./ChannelGitProtectionRulesEdit";
import type { ChannelLookupData } from "./CreateOrEditChannelPageContent";
import { getDeploymentActionGitDependenciesWithDetails } from "./GitProtectionRules/getDeploymentActionGitDependenciesWithDetails";
import styles from "./style.module.less";
interface CreateOrEditChannelFormProps {
    doBusyTask: DoBusyTask;
    model: ChannelResource;
    setModel: (value: SetStateAction<ChannelResource>) => void;
    getFieldError: (fieldName: string) => string | undefined;
    lifecycleOptions: LifecycleResource[];
    project: ProjectResource;
    channel: ChannelResource | "create new channel";
    gitRefValidationError: ValidateGitRefV2Response | undefined;
    gitRef: Readonly<GitRefResource> | undefined;
    lookupData: ChannelLookupData;
}
export function CreateOrEditChannelForm({ doBusyTask, model, setModel, getFieldError, lifecycleOptions, channel, project, gitRefValidationError, gitRef, lookupData: { deploymentActions, tagIndex, parentEnvironments }, }: CreateOrEditChannelFormProps) {
    const isCreatingNewChannel = channel === "create new channel";
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const deploymentActionError = deploymentActions instanceof OctopusError ? deploymentActions : undefined;
    const deploymentActionsOrEmpty = deploymentActions instanceof OctopusError ? [] : deploymentActions;
    const packageActions = deploymentActionPackages(deploymentActionsOrEmpty);
    const gitDependencyActions = getDeploymentActionGitDependenciesWithDetails(deploymentActionsOrEmpty);
    const updateRuleProperty = useCallback(<K extends keyof ChannelVersionRuleResource>(versionRuleIndex: number, propertyName: K, value: ChannelVersionRuleResource[K]) => setModel((prev) => ({
        ...prev,
        Rules: prev.Rules.map((rule, i) => {
            if (i === versionRuleIndex) {
                return {
                    ...rule,
                    [propertyName]: value,
                };
            }
            return rule;
        }),
    })), [setModel]);
    const updateRule = useCallback((index: number, rule: ChannelVersionRuleResource) => setModel((prev) => ({
        ...prev,
        Rules: prev.Rules.map((existingRule, i) => {
            if (i === index) {
                return rule;
            }
            return existingRule;
        }),
    })), [setModel]);
    const handleVersionRuleDeleteByIndex = useCallback((index: number) => setModel((prev) => ({
        ...prev,
        Rules: prev.Rules.filter((_, i) => i !== index),
    })), [setModel]);
    const EphemeralEnvironmentNameTemplateExample = (exampleName: string) => (<a href="#" onClick={(e) => {
            e.preventDefault();
            setModel((prev) => ({
                ...prev,
                EphemeralEnvironmentNameTemplate: exampleName,
            }));
        }}>
            {exampleName}
        </a>);
    return (<>
            {ephemeralEnvironmentsAreEnabled && (<ExpandableFormSection errorKey="Type" title="Channel Type" focusOnExpandAll summary={model.Type === "EphemeralEnvironment" ? Summary.summary("Releases in this channel will provision a new Ephemeral Environment") : Summary.summary("Releases in this channel will progress through the selected Lifecycle")} help="Select the type of channel.">
                    <RadioButtonGroup value={model.Type} onChange={(Type) => setModel((prev) => ({ ...prev, Type }))} title="Channel Type">
                        <RadioButton label="Lifecycle" value="Lifecycle" disabled={!isCreatingNewChannel}/>
                        <Note>Releases in this channel will progress through the selected Lifecycle</Note>
                        <RadioButton label="Ephemeral Environment" value="EphemeralEnvironment" disabled={!isCreatingNewChannel}/>
                        <Note>Releases in this channel will be deployed to ephemeral environments. The environment will be provisioned automatically when it is first deployed to</Note>
                    </RadioButtonGroup>
                </ExpandableFormSection>)}
            <ExpandableFormSection errorKey="Name" title="Name" focusOnExpandAll summary={model.Name ? Summary.summary(<NameSummaryWithSlug name={model.Name} slug={model.Slug}/>) : Summary.placeholder("Please enter a name for your channel")} help="Enter a name for your channel.">
                <Text value={model.Name || ""} onChange={(Name) => setModel((prev) => ({ ...prev, Name }))} label="Channel name" validate={required("Please enter a channel name")} error={getFieldError("Name")} autoFocus={true}/>
                <Note>
                    A short, memorable, unique name for this channel. Example: <em>1.x Normal, 2.x Beta</em>
                </Note>

                {!isCreatingNewChannel && (<SlugEditor value={model.Slug || ""} name={model.Name} originalSlug={channel.Slug ?? ""} onChange={(Slug) => setModel((prev) => ({ ...prev, Slug }))} label="Channel slug" validate={required("Please enter a channel slug")} error={getFieldError("Slug")}/>)}
            </ExpandableFormSection>
            <ExpandableFormSection errorKey="description" title="Description" summary={descriptionSummary(model)} help="Enter a description for your channel.">
                <MarkdownEditor value={model.Description} label="Channel description" onChange={(Description) => setModel((prev) => ({ ...prev, Description }))}/>
            </ExpandableFormSection>
            {model.Type !== "EphemeralEnvironment" && (<ExpandableFormSection errorKey="lifecycle" title="Lifecycle" summary={lifecycleSummary(model, lifecycleOptions)} help="Select a lifecycle for your channel.">
                    <DeprecatedSelect value={model.LifecycleId || inheritFromProjectLifecycleId} onChange={(lifecycleId) => setModel((prev) => ({
                ...prev,
                LifecycleId: lifecycleId === inheritFromProjectLifecycleId ? null! : lifecycleId!,
            }))} items={lifecycleOptions.map((pg) => ({ value: pg.Id, text: pg.Name }))} label="Lifecycle" sortItems={false}/>
                    <Note>
                        The lifecycle defines how releases can be promoted between environments. Lifecycles can be defined <InternalLink to={links.lifecyclesPage.generateUrl({ spaceId: project.SpaceId })}>here</InternalLink>. If no lifecycle is
                        selected, the default lifecycle for the project will be used.
                    </Note>
                </ExpandableFormSection>)}
            <ExpandableFormSection errorKey="defaultChannel" title="Is Default Channel" summary={model.IsDefault
            ? Summary.summary(<span>
                                  <strong>This channel</strong> will be chosen by default when creating releases
                              </span>)
            : Summary.summary(<span>
                                  <strong>A different channel</strong> will be chosen by default when creating releases
                              </span>)} help="This channel will be selected by default when creating releases.">
                <Checkbox label="Default channel" value={model.IsDefault} onChange={(IsDefault) => setModel((prev) => ({ ...prev, IsDefault }))}/>
            </ExpandableFormSection>
            <ExpandableFormSection errorKey="customFieldDefinitions" title="Custom Fields" fillCardWidth={CardFill.FillRight} summary={model.CustomFieldDefinitions.length > 0
            ? Summary.summary(<span>
                                  {model.CustomFieldDefinitions.length} custom field{model.CustomFieldDefinitions.length === 1 ? "" : "s"} defined
                              </span>)
            : Summary.placeholder("No custom fields defined")} help="Set required custom fields for releases in this channel.">
                <ChannelCustomFieldDefinitionsEdit definitions={model.CustomFieldDefinitions} onChange={(customFieldDefinitions) => setModel((prev) => ({ ...prev, CustomFieldDefinitions: customFieldDefinitions }))}/>
            </ExpandableFormSection>
            {ephemeralEnvironmentsAreEnabled && model.Type === "EphemeralEnvironment" && (<>
                    <FormSectionHeading title="Ephemeral Environments"/>
                    <ExpandableFormSection errorKey="lifecycle" title="Parent Environment" summary={parentEnvironmentSummary(model.ParentEnvironmentId, parentEnvironments)} help="Select a parent environment for your channel." contextualHelp={<ParentEnvironmentInfoPopover />}>
                        <DeprecatedSelect value={model.ParentEnvironmentId ?? undefined} onChange={(parentEnvironmentId) => setModel((prev) => ({
                ...prev,
                ParentEnvironmentId: parentEnvironmentId ?? null,
            }))} items={parentEnvironments.map((pe) => ({ value: pe.Id, text: pe.Name }))} label="Parent Environment" sortItems={true}/>
                    </ExpandableFormSection>
                    <ExpandableFormSection errorKey="autoDeployment" title="Auto Deployment" help="Choose whether releases are automatically deployed to ephemeral environment" summary={model.AutomaticEphemeralEnvironmentDeployments ? Summary.summary(<span>Automatically deploy to ephemeral environments</span>) : Summary.summary(<span>Don't automatically deploy to ephemeral environments</span>)}>
                        <RadioGroupNew value={model.AutomaticEphemeralEnvironmentDeployments ?? true} onChange={(AutomaticEphemeralEnvironmentDeployments) => {
                setModel((prev) => ({
                    ...prev,
                    AutomaticEphemeralEnvironmentDeployments,
                    EphemeralEnvironmentNameTemplate: AutomaticEphemeralEnvironmentDeployments ? prev.EphemeralEnvironmentNameTemplate : "",
                }));
            }} label={"Automatic Deployments"} description={"Choose"} hideLabel={true} hideDescription={true}>
                            <RadioButtonNew value={true} label="Automatically deploy to ephemeral environments" hasDefaultMarker={false} name={"AutomaticDeployments"}/>
                            <Note>Ephemeral environments will be created and deployed automatically from releases</Note>
                            <RadioButtonNew value={false} label="Don't automatically deploy to ephemeral environments" name={"ManualDeployments"}/>
                            <Note>Ephemeral environments need to be created and releases deployed manually</Note>
                        </RadioGroupNew>
                        <Note>
                            <ExternalLink href={"ephemeralenvironments"}>Learn about automatic deployments</ExternalLink>.
                        </Note>
                    </ExpandableFormSection>
                    {(model.AutomaticEphemeralEnvironmentDeployments ?? true) && (<ExpandableFormSection errorKey="EphemeralEnvironmentNameTemplate" title="Environment Name Template" summary={ephemeralEnvironmentNameTemplateSummary(model)} help="Enter a name template for Ephemeral Environments created from this channel.">
                            <Text value={model.EphemeralEnvironmentNameTemplate || ""} label="Environment Name Template" onChange={(EphemeralEnvironmentNameTemplate) => setModel((prev) => ({ ...prev, EphemeralEnvironmentNameTemplate }))}/>
                            <Note>
                                Example:{" "}
                                {project.PersistenceSettings.Type === PersistenceSettingsType.VersionControlled
                    ? EphemeralEnvironmentNameTemplateExample("#{Octopus.Release.Git.BranchName}")
                    : EphemeralEnvironmentNameTemplateExample("#{Octopus.Release.CustomFields[My.Custom.Field]}")}
                            </Note>
                            <NameTemplateInformation />
                        </ExpandableFormSection>)}
                </>)}
            <FormSectionHeading title="Package Version Rules"/>
            {gitRefValidationError && (<WarningPanel title={`An error has occurred showing steps that use packages in the deployment process: ${gitRefValidationError.Message}. Please select a different branch or tag from the deployment process screen to view steps.`} warnings={[]}/>)}
            {deploymentActionError && <WarningPanel title={`An error has occurred showing steps that use packages in the deployment process: ${deploymentActionError.ErrorMessage}`} warnings={deploymentActionError.Errors ?? []}/>}
            <RemovableExpandersList helpElement={<div>
                        Use package version rules to restrict which versions of packages can be used on steps when creating releases in this channel.
                        <Note>
                            Learn about <ExternalLink href={"ChannelVersionRules"}>channel version rules</ExternalLink>.
                        </Note>
                    </div>} typeDisplayName="Package Version Rule" data={model.Rules} listActions={[
            <ActionButton key="AddVersion" label="Add rule" onClick={() => setModel((prev) => ({
                    ...prev,
                    Rules: [
                        ...prev.Rules,
                        {
                            Id: null!,
                            Tag: "",
                            VersionRange: "",
                            ActionPackages: [],
                            Links: null!,
                        },
                    ],
                }))}/>,
        ]} onRow={(item: ChannelVersionRuleResource, index: number) => {
            return (<VersionRule model={model} packageActions={packageActions} deploymentActions={deploymentActions} item={item} versionRuleIndex={index} hasLoadedSteps={deploymentActionError === undefined} project={project} gitRef={gitRef} gitRefValidationError={gitRefValidationError} updateRuleProperty={updateRuleProperty} updateRule={updateRule}/>);
        }} onRowSummary={(item: ChannelVersionRuleResource) => {
            return ruleSummary(item);
        }} onRowHelp={() => {
            return "Use package version rules to restrict which versions of packages can be used on steps when creating releases in this channel.";
        }} onRemoveRowByIndex={handleVersionRuleDeleteByIndex}/>

            <ChannelGitProtectionRulesEdit project={project} channel={model} gitRef={gitRef} gitRefValidationError={gitRefValidationError} gitDependencyActions={gitDependencyActions} deploymentActionsError={deploymentActionError} doBusyTask={doBusyTask} onGitReferenceRulesChanged={(GitReferenceRules) => setModel((prev) => ({ ...prev, GitReferenceRules }))} onGitResourceRulesChanged={(GitResourceRules) => setModel((prev) => ({ ...prev, GitResourceRules }))}/>

            <FeatureToggle feature={Feature.MultiTenancy}>
                {(project.TenantedDeploymentMode !== TenantedDeploymentMode.Untenanted || model.TenantTags.length > 0) && model.Type === "Lifecycle" && (<React.Fragment>
                        <FormSectionHeading title="Tenants"/>
                        <ExpandableFormSection errorKey="tenantTags" title="Tenants" summary={model.TenantTags.length > 0
                ? Summary.summary(<span>
                                              Releases in this channel can only be deployed to certain tenants:
                                              <TagsList canonicalNames={model.TenantTags} tagIndex={tagIndex}/>
                                          </span>)
                : Summary.default("Releases in this channel can be deployed to any tenants")} help={"Choose which tenants the releases in this channel apply to."}>
                            <Note>
                                Releases in this channel will only be deployed to tenants matching this filter. Clear the filter to make releases in this channel available to all tenants. Learn about{" "}
                                <ExternalLink href={"TenantsAndChannels"}>tenants and channels</ExternalLink>.
                            </Note>
                            <AdvancedTenantTagsSelector emptyFilterMeansAllTenants={true} selectedTenantTags={model.TenantTags} doBusyTask={doBusyTask} onChange={(TenantTags) => setModel((prev) => ({ ...prev, TenantTags }))} showPreviewButton={true}/>
                        </ExpandableFormSection>
                    </React.Fragment>)}
            </FeatureToggle>
        </>);
}
const inheritFromProjectLifecycleId = "-1";
interface VersionRuleProps {
    model: ChannelResource;
    packageActions: DeploymentActionPackageResource[];
    deploymentActions: DeploymentActionResource[] | OctopusError;
    item: ChannelVersionRuleResource;
    versionRuleIndex: number;
    hasLoadedSteps: boolean;
    project: ProjectResource;
    gitRefValidationError: ValidateGitRefV2Response | undefined;
    gitRef: Readonly<GitRefResource> | undefined;
    updateRuleProperty: <K extends keyof ChannelVersionRuleResource>(versionRuleIndex: number, propertyName: K, value: ChannelVersionRuleResource[K]) => void;
    updateRule: (index: number, rule: ChannelVersionRuleResource) => void;
}
function VersionRule({ versionRuleIndex, item, hasLoadedSteps, model, packageActions, deploymentActions, project, gitRefValidationError, gitRef, updateRule, updateRuleProperty }: VersionRuleProps) {
    const allSelectedActionPackages = model.Rules.map((r) => r.ActionPackages).reduce((a, b) => a.concat(b), []); // All of the selected packages across all version rules
    function actionIsEqual(a: DeploymentActionPackageResource, b: DeploymentActionPackageResource) {
        return a.DeploymentAction === b.DeploymentAction && (a.PackageReference || "") === (b.PackageReference || "");
    }
    // Once a package is selected it shouldn't show for any other version rules. This is the list
    // of all packages that are not currently selected in any version rules for this channel.
    const autoCompleteActionPackages = packageActions
        .filter((a) => !allSelectedActionPackages.some((b) => actionIsEqual(a, b)))
        .map((x) => new DeploymentActionPackageReferenceDataItem(x, String(packageActions.findIndex((y) => actionIsEqual(x, y)))));
    const itemSelectedActionPackages = packageActions.filter((a) => item.ActionPackages.some((b) => actionIsEqual(a, b))).map((x) => new DeploymentActionPackageReferenceDataItem(x, String(packageActions.findIndex((y) => actionIsEqual(x, y)))));
    // All of the packages that are not available in any steps. This will only be populated for
    // version controlled projects where steps might not exist on the current selected branch.
    const selectedActionPackagesNotInTheProcess = item.ActionPackages.filter((a) => !autoCompleteActionPackages.some((b) => actionIsEqual(a, b.ActionPackage)) && !itemSelectedActionPackages.some((b) => actionIsEqual(a, b.ActionPackage))).map((x) => new DeploymentActionPackageReferenceDataItem(x, displayName(x), false));
    const allSelectedActionPackagesForRule = [...itemSelectedActionPackages, ...selectedActionPackagesNotInTheProcess];
    const deploymentActionsOrEmpty = deploymentActions instanceof OctopusError ? [] : deploymentActions;
    const deploymentActionError = deploymentActions instanceof OctopusError ? deploymentActions : undefined;
    return (<div>
            <DeploymentActionPackageMultiSelect disabled={!hasLoadedSteps} error={deploymentActionError === undefined ? undefined : "Error loading package steps"} items={[...autoCompleteActionPackages, ...allSelectedActionPackagesForRule]} value={allSelectedActionPackagesForRule.map((x) => x.Id)} label="Package step(s)" onChange={(actionPackageIndexes) => {
            return updateRuleProperty(versionRuleIndex, "ActionPackages", actionPackageIndexes.map((i) => {
                const actionPackageIndex = Number(i);
                if (Number.isNaN(actionPackageIndex)) {
                    // Action packages references for steps that are not in the process will have the action + package
                    // name as the identifier (Action Name/PackageName). This will only ever happen for version controlled
                    // processes. We need to keep the selection in place, or the save will wipe the rules that were set
                    // on a different branch.
                    return selectedActionPackagesNotInTheProcess.find((ap) => displayName(ap.ActionPackage) == i)!.ActionPackage;
                }
                else {
                    return packageActions[actionPackageIndex];
                }
            }));
        }} openOnFocus={false} project={project} gitRef={gitRef}/>
            {project.IsVersionControlled && deploymentActionError === undefined && !gitRefValidationError && renderMessage(gitRef)}
            <Text value={item.VersionRange || ""} onChange={(value) => updateRuleProperty(versionRuleIndex, "VersionRange", value)} label="Version range"/>
            <Note>
                Use the <ExternalLink href="NuGetVersioning">NuGet</ExternalLink> or <ExternalLink href="MavenVersioning">Maven</ExternalLink> versioning syntax (depending on the feed type) to specify the range of versions to include.
            </Note>
            <Text value={item.Tag || ""} onChange={(value) => updateRuleProperty(versionRuleIndex, "Tag", value)} label="Pre-release tag"/>
            <Note>
                A regular-expression which will select on the <ExternalLink href="NuGetVersioning">SemVer</ExternalLink> pre-release tag or the <ExternalLink href="MavenVersionParser"> Maven</ExternalLink> qualifier.
            </Note>
            <Note>
                Check our <ExternalLink href="ChannelVersionRuleTags">documentation</ExternalLink> for more information on tags along with examples.
            </Note>
            <div className={styles.designRuleButton}>
                <OpenDialogButton label="Design rule">
                    <DesignRule model={item} project={project} deploymentActions={deploymentActionsOrEmpty} onOkClick={(rule) => updateRule(versionRuleIndex, rule)}/>
                </OpenDialogButton>
            </div>
        </div>);
}
function ruleSummary(rule: ChannelVersionRuleResource) {
    const summarySpan = (<span>
            Applies to <strong>{rule.ActionPackages.map((pkg) => displayName(pkg)).join(", ")}</strong>
            &nbsp;with a version range matching <strong>{rule.VersionRange}</strong>
            {!!rule.Tag && (<span>
                    {" "}
                    and a pre-release tag matching <strong>{rule.Tag}</strong>
                </span>)}
        </span>);
    return Summary.summary(summarySpan);
}
function lifecycleSummary(channel: ChannelResource, lifecycles: LifecycleResource[]) {
    if (channel.LifecycleId) {
        return Summary.summary(lifecycles.find((l) => l.Id === channel.LifecycleId)?.Name);
    }
    return Summary.default("Inherited from project");
}
function parentEnvironmentSummary(parentEnvironmentId: string | null, parentEnvironments: ParentEnvironmentV2Resource[]) {
    if (!parentEnvironmentId) {
        return Summary.placeholder("Select a parent environment for your channel");
    }
    const selectedParentEnvironment = parentEnvironments.find((pe) => pe.Id === parentEnvironmentId);
    return Summary.summary(selectedParentEnvironment ? selectedParentEnvironment.Name : parentEnvironmentId);
}
function renderMessage(gitRef: Readonly<GitRefResource> | undefined) {
    const gitRefCanonical = gitRef?.CanonicalName;
    let gitRefType = "branch";
    if (isGitCommit(gitRefCanonical)) {
        gitRefType = "commit";
    }
    else if (isGitTag(gitRefCanonical)) {
        gitRefType = "tag";
    }
    return (<Note>
            Showing steps from the <code>{gitRef?.Name}</code> {gitRefType}. A different {gitRefType} can be selected from the deployment process screen.
        </Note>);
}
function descriptionSummary(channel: ChannelResource) {
    return channel.Description ? Summary.summary(<Markdown markup={channel.Description}/>) : Summary.placeholder("No channel description provided");
}
function ephemeralEnvironmentNameTemplateSummary(channel: ChannelResource) {
    return channel.EphemeralEnvironmentNameTemplate ? Summary.summary(channel.EphemeralEnvironmentNameTemplate) : Summary.placeholder("No template provided");
}
const NameTemplateInformation: React.FC = () => {
    return (<MoreInfo showLabel="Show name template information" hideLabel="Hide name template information">
            <div>
                <h4>Getting Started</h4>
                <p>You can use variables from your release in your ephemeral environment name using Octostache expressions like this:</p>
                <pre>
                    <code>{"#{Octopus.Release.Number}"}</code>
                </pre>
                <p>You can also set Custom Fields on your release and use these in your ephemeral environment names like this:</p>
                <pre>
                    <code>{"#{Octopus.Release.CustomFields[My.Custom.Field]}"}</code>
                </pre>
                <Note>
                    <ExternalLink href="systemvariables">See system variables documentation</ExternalLink>
                </Note>
                <hr />
                <h4>Name Template Tips</h4>
                <ul style={{ listStyleType: "disc", marginLeft: "1.5em", display: "flex", flexDirection: "column", gap: "1em" }}>
                    <li>Ensure that your name template evaluates to a string of 50 characters or less.</li>
                    <li>If your environment name contains any of the following characters, each character will be replaced with a dash.</li>
                    <pre>
                        <code>{"< > : \" / \\ | ? * { }"}</code>
                    </pre>
                    <li>Leading and trailing dashes and underscores will be removed from your environment name.</li>
                </ul>
                <hr />
                <h4>Example Templates</h4>
                <p>
                    If your release has a Custom Field <code>{"PullRequestNumber"}</code> with a value of <code>{"301"}</code> taken from your pull request, a name template of:
                </p>
                <pre>
                    <code>{"PR-#{Octopus.Release.CustomFields[PullRequestNumber]}"}</code>
                </pre>
                <p>Would give you an environment name of:</p>
                <pre>
                    <code>{"PR-301"}</code>
                </pre>
                <p>
                    If the same release was created from branch <code>{"ava/new-feature"}</code> using Config as Code in Octopus, a name template of:
                </p>
                <pre>
                    <code>{"PR-#{Octopus.Release.CustomFields[PullRequestNumber]}-#{Octopus.Release.Git.Branch}"}</code>
                </pre>
                <p>Would give you an environment name of:</p>
                <pre>
                    <code>{"PR-301-ava-new-feature"}</code>
                </pre>
            </div>
        </MoreInfo>);
};
NameTemplateInformation.displayName = "NameTemplateInformation"
